import React, { useMemo, useState } from 'react';
import useAppStore from '../../store';
import CommandPrompt from '../../components/console/CommandPrompt';
import LogStreamPanel from '../../components/console/LogStreamPanel';
import LogFilterBar from '../../components/console/LogFilterBar';
import EmergencyControls from '../../components/console/EmergencyControls';
import AuditTrailTable from '../../components/console/AuditTrailTable';
import useConsoleLogStream from '../../hooks/useConsoleLogStream';

const TIME_WINDOWS = [
  { label: 'All', ms: null },
  { label: '5m', ms: 5 * 60 * 1000 },
  { label: '1m', ms: 60 * 1000 }
];

const PAUSED_LABEL = 'Paused — filters frozen while paused';

function ConsolePage() {
  const agents = useAppStore((state) => state.agents);
  const logs = useAppStore((state) => state.logs);
  const [filters, setFilters] = useState({
    level: 'all',
    agentId: 'all',
    query: '',
    windowMs: null,
    paused: false
  });

  const feedMode = useConsoleLogStream({ paused: filters.paused });

  const filteredLogs = useMemo(() => {
    const cutoff = filters.windowMs ? Date.now() - filters.windowMs : null;
    const query = filters.query.trim().toLowerCase();

    return logs.filter((entry) => {
      if (filters.level !== 'all' && entry.level !== filters.level) return false;
      if (filters.agentId !== 'all' && String(entry.agentId ?? '') !== filters.agentId) return false;
      if (cutoff && entry.timestamp < cutoff) return false;
      if (query && !entry.message.toLowerCase().includes(query)) return false;
      return true;
    });
  }, [logs, filters]);

  return (
    <main className="page-content console-page">
      <section className="page-heading">
        <div>
          <p className="eyebrow">POWER USER TOOLING</p>
          <h1>Console</h1>
          <span>Command the runtime, stream agent logs, control emergencies.</span>
        </div>
        <div className="console-page__feedmeta">
          <span className={`console-feed-badge console-feed-badge--${feedMode}`}>
            {feedMode === 'live' ? 'LIVE SSE' : feedMode === 'demo' ? 'DEMO STREAM' : 'CONNECTING…'}
          </span>
        </div>
      </section>

      <EmergencyControls />

      <section className="console-panel">
        <div className="console-panel__head">
          <LogFilterBar
            agents={agents}
            filters={filters}
            onChange={(next) => setFilters((current) => ({ ...current, ...next }))}
          />
          <div className="console-panel__window">
            {TIME_WINDOWS.map((window) => (
              <button
                className={filters.windowMs === window.ms ? 'filter-chip is-active' : 'filter-chip'}
                key={window.label}
                onClick={() => setFilters((current) => ({ ...current, windowMs: window.ms }))}
                type="button"
              >
                {window.label}
              </button>
            ))}
            <button
              className={filters.paused ? 'filter-chip is-active' : 'filter-chip'}
              onClick={() => setFilters((current) => ({ ...current, paused: !current.paused }))}
              title={PAUSED_LABEL}
              type="button"
            >
              {filters.paused ? 'Paused' : 'Pause'}
            </button>
          </div>
        </div>

        <LogStreamPanel feedMode={feedMode} visibleLogs={filteredLogs} />

        <CommandPrompt />
      </section>

      <section className="panel">
        <div className="section-heading">
          <div>
            <h2>Audit trail</h2>
            <span>Every console command and emergency action, newest first.</span>
          </div>
          <span className="console-page__count">{filteredLogs.length.toLocaleString()} lines match filters</span>
        </div>
        <AuditTrailTable />
      </section>
    </main>
  );
}

export default ConsolePage;
